import { Box, Skeleton } from "@mui/material";
import React from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";

/**
 * Loading layout for the week, shaped like the real day rows so the page does
 * not jump when the plan arrives: a date and kcal header, then up to three
 * activity cards (icon disc, two text lines, a badge).
 */
const WorkoutPlanSkeleton = () => {
  const theme: Theme = useTheme();

  const block = (sx: object) => (
    <Skeleton variant="rounded" animation="pulse" sx={{ ...styles.block(theme), ...sx }} />
  );

  const card = (key: number) => (
    <Box key={key} sx={styles.card(theme)}>
      <Box sx={styles.inline}>
        {block({ width: "32px", height: "32px", borderRadius: "16px" })}
        {block({ width: "60%", height: "16px" })}
      </Box>
      {block({ width: "70%", height: "14px" })}
      {block({ width: "40%", height: "14px" })}
    </Box>
  );

  const row = (key: number, cards: number) => (
    <Box key={key} sx={styles.row(theme)}>
      <Box sx={styles.inline}>
        {block({ width: "160px", height: "16px" })}
        {block({ width: "80px", height: "14px" })}
      </Box>
      <Box sx={styles.cards}>
        {Array.from({ length: cards }, (_, index) => card(index))}
      </Box>
    </Box>
  );

  return (
    <Box sx={styles.container}>
      {row(0, 1)}
      {row(1, 2)}
      {row(2, 1)}
    </Box>
  );
};

export default WorkoutPlanSkeleton;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
    // Mirror WeekPlanView's ScrollableWrapper padding + gutter.
    paddingRight: "16px",
  },
  block: (theme: Theme) => ({
    bgcolor: theme.colorTokens.N[30],
    flexShrink: 0,
  }),
  // Same shell as WeekDayRow.
  row: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    gap: "12px",
    padding: "16px",
    borderRadius: "16px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
  }),
  inline: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "16px",
  },
  // Same shell as the activity cards.
  card: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    gap: "10px",
    padding: "16px",
    borderRadius: "16px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
  }),
};
